import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Plus, Loader2, AlertTriangle, X } from 'lucide-react';
import { cn, localToday } from '@/lib/utils';
import { ClientSearchInput } from './ClientSearchInput';
import { AddClientDialog } from '@/pages/ClientsPage';

export function AddOrderDialog({ open, onOpenChange, onSuccess, preselectedClient = null, preselectedDate = '', preselectedTime = '', preselectedWorker = null }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conflicts, setConflicts] = useState([]);
  const [clients, setClients] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  const [createClientOpen, setCreateClientOpen] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    service_type_ids: [],
    title: '',
    description: '',
    scheduled_date: '',
    scheduled_time_start: '',
    scheduled_time_end: '',
    duration_minutes: '',
    price: '',
    address_street: '',
    address_city: '',
    address_postal: '',
    priority: 'normal',
    assigned_worker_ids: [],
    payment_method_id: '',
  });

  const calcEndTime = (start, mins) => {
    if (!start || !mins) return '';
    const [h, m] = start.split(':').map(Number);
    const total = h * 60 + m + parseInt(mins);
    return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  const formatPostal = (val) => {
    const clean = val.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (clean.length <= 3) return clean;
    return clean.slice(0, 3) + ' ' + clean.slice(3, 6);
  };

  // Check for conflicts when worker, date, or time changes
  const checkConflicts = async (workerIds, date, timeStart, timeEnd) => {
    if (!workerIds?.length || !date || !timeStart) {
      setConflicts([]);
      return;
    }
    
    try {
      const response = await api.post('/orders/check-conflicts', {
        worker_ids: workerIds,
        scheduled_date: date,
        time_start: timeStart,
        time_end: timeEnd || null,
      });
      setConflicts(response.data.conflicts || []);
    } catch (err) {
      console.error('Failed to check conflicts:', err);
      setConflicts([]);
    }
  };

  // Trigger conflict check when relevant fields change
  useEffect(() => {
    const timer = setTimeout(() => {
      checkConflicts(
        formData.assigned_worker_ids,
        formData.scheduled_date,
        formData.scheduled_time_start,
        formData.scheduled_time_end
      );
    }, 500); // Debounce
    
    return () => clearTimeout(timer);
  }, [formData.assigned_worker_ids, formData.scheduled_date, formData.scheduled_time_start, formData.scheduled_time_end]);

  // Set today's date as default when dialog opens
  useEffect(() => {
    if (open) {
      const today = localToday();
      setFormData(prev => ({
        ...prev,
        scheduled_date: prev.scheduled_date || today,
        scheduled_time_start: prev.scheduled_time_start || '08:00',
      }));
    }
  }, [open]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsRes, servicesRes, workersRes, paymentsRes] = await Promise.all([
          api.get('/clients'),
          api.get('/service-types'),
          api.get('/workers'),
          api.get('/payment-methods'),
        ]);
        setClients(clientsRes.data);
        setServiceTypes(servicesRes.data);
        setWorkers(workersRes.data);
        setPaymentMethods(paymentsRes.data);
      } catch (err) {
        console.error('Failed to load form data:', err);
      }
    };
    if (open) loadData();
  }, [open, api]);

  // Set preselected client
  useEffect(() => {
    if (preselectedClient && open) {
      setFormData(prev => ({
        ...prev,
        client_id: preselectedClient.id,
        address_street: preselectedClient.address?.street || '',
        address_city: preselectedClient.address?.city || '',
        address_postal: preselectedClient.address?.postal_code || '',
      }));
    }
  }, [preselectedClient, open]);

  // Set preselected date and time from calendar
  useEffect(() => {
    if (open && (preselectedDate || preselectedTime)) {
      setFormData(prev => ({
        ...prev,
        scheduled_date: preselectedDate || prev.scheduled_date,
        scheduled_time_start: preselectedTime || prev.scheduled_time_start,
      }));
    }
  }, [open, preselectedDate, preselectedTime]);

  // Set preselected worker from calendar
  useEffect(() => {
    if (open && preselectedWorker) {
      setFormData(prev => ({
        ...prev,
        assigned_worker_ids: [preselectedWorker.id],
      }));
    }
  }, [open, preselectedWorker]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // VALIDATION: Client is required
    if (!formData.client_id) {
      setError('Выберите клиента');
      setLoading(false);
      return;
    }

    // VALIDATION: Block if there are conflicts
    if (conflicts.length > 0) {
      setError('Нельзя создать заказ: у работника конфликт времени');
      setLoading(false);
      return;
    }

    try {
      // Auto-generate title from services and client
      const selectedServices = serviceTypes.filter(s => formData.service_type_ids.includes(s.id));
      const client = clients.find(c => c.id === formData.client_id);
      const autoTitle = selectedServices.map(s => s.name).join(', ') || 'Заказ';
      const clientSuffix = client?.name ? ` — ${client.name}` : '';

      const payload = {
        ...formData,
        title: formData.title || `${autoTitle}${clientSuffix}`,
        price: parseFloat(formData.price) || 0,
        scheduled_date: formData.scheduled_date ? new Date(formData.scheduled_date).toISOString() : null,
        service_type_ids: formData.service_type_ids,
        service_type_id: formData.service_type_ids[0] || null,
        payment_method_id: formData.payment_method_id || null,
      };
      
      await api.post('/orders', payload);
      
      // Close dialog and reset form first
      onOpenChange(false);
      setFormData({
        client_id: '',
        service_type_ids: [],
        title: '',
        description: '',
        scheduled_date: '',
        scheduled_time_start: '',
        scheduled_time_end: '',
        duration_minutes: '',
        price: '',
        address_street: '',
        address_city: '',
        address_postal: '',
        priority: 'normal',
        assigned_worker_ids: [],
        payment_method_id: '',
      });
      
      // Call onSuccess after dialog closed (errors here shouldn't show in dialog)
      try {
        await onSuccess?.();
      } catch (refreshErr) {
        console.error('Error refreshing data:', refreshErr);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const toggleWorker = (workerId) => {
    setFormData(prev => ({
      ...prev,
      assigned_worker_ids: prev.assigned_worker_ids.includes(workerId)
        ? prev.assigned_worker_ids.filter(id => id !== workerId)
        : [...prev.assigned_worker_ids, workerId]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-auto bottom-0 translate-x-0 translate-y-0 sm:top-[50%] sm:translate-y-[-50%] sm:translate-x-[-50%] sm:bottom-auto left-0 right-0 sm:left-[50%] sm:right-auto w-full sm:w-auto sm:max-w-2xl rounded-t-2xl rounded-b-none sm:rounded-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Новый заказ</DialogTitle>
          <DialogDescription>
            Создайте заказ для клиента
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}
            
            {/* Client Selection with Search */}
            <div className="space-y-2">
              <Label>Клиент *</Label>
              <ClientSearchInput
                clients={clients}
                value={formData.client_id}
                onChange={(clientId, client) => {
                  setFormData(prev => ({
                    ...prev,
                    client_id: clientId,
                    address_street: client?.address?.street || '',
                    address_city: client?.address?.city || '',
                    address_postal: client?.address?.postal_code || '',
                  }));
                }}
                onCreateNew={() => setCreateClientOpen(true)}
                data-testid="order-client"
              />
            </div>

            {/* Services (multiple) */}
            <div className="space-y-2">
              <Label>Услуги</Label>

              {/* Selected services list */}
              {formData.service_type_ids.map((sid, idx) => {
                const svc = serviceTypes.find(s => s.id === sid);
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <Select
                      value={sid}
                      onValueChange={(v) => {
                        const ids = [...formData.service_type_ids];
                        ids[idx] = v;
                        setFormData(prev => ({...prev, service_type_ids: ids}));
                      }}
                    >
                      <SelectTrigger className="flex-1" data-testid={`order-service-${idx}`}>
                        <SelectValue placeholder="Выберите услугу" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceTypes.map(s => (
                          <SelectItem key={s.id} value={s.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                              {s.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        service_type_ids: prev.service_type_ids.filter((_, i) => i !== idx),
                      }))}
                    >
                      <X className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                );
              })}

              {/* Add service button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  service_type_ids: [...prev.service_type_ids, ''],
                }))}
                data-testid="order-add-service"
              >
                <Plus className="h-4 w-4 mr-2" />
                Добавить услугу
              </Button>
            </div>

            {/* Price */}
            <div className="space-y-2">
              <Label>Стоимость ({t('currency.symbol')})</Label>
              <Input
                type="number"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({...prev, price: e.target.value}))}
                placeholder="Укажите стоимость заказа"
                data-testid="order-price"
              />
              <p className="text-xs text-muted-foreground">Укажите стоимость для данного объекта</p>
            </div>

            {/* Date + Time start — two equal columns, no overflow */}
            <div className="grid grid-cols-2 gap-2 min-w-0">
              <div className="space-y-1 min-w-0">
                <Label className="text-xs">Дата</Label>
                <input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData(prev => ({...prev, scheduled_date: e.target.value}))}
                  data-testid="order-date"
                  className="w-full min-w-0 rounded-md border border-input bg-background px-2 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
              <div className="space-y-1 min-w-0">
                <Label className="text-xs">Время начала</Label>
                <input
                  type="time"
                  value={formData.scheduled_time_start}
                  min="07:00"
                  max="18:00"
                  step="600"
                  onChange={(e) => {
                    const start = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      scheduled_time_start: start,
                      scheduled_time_end: calcEndTime(start, prev.duration_minutes),
                    }));
                  }}
                  data-testid="order-time-start"
                  className="w-full min-w-0 rounded-md border border-input bg-background px-2 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            </div>

            {/* Duration — presets + dropdowns */}
            <div className="space-y-2">
              <Label>Продолжительность</Label>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { label: '1ч', min: 60 },
                  { label: '1ч 30м', min: 90 },
                  { label: '2ч', min: 120 },
                  { label: '2ч 30м', min: 150 },
                  { label: '3ч', min: 180 },
                  { label: '4ч', min: 240 },
                ].map(p => (
                  <Button
                    key={p.min}
                    type="button"
                    variant={parseInt(formData.duration_minutes) === p.min ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 text-xs px-2.5"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      duration_minutes: p.min,
                      scheduled_time_end: calcEndTime(prev.scheduled_time_start, p.min),
                    }))}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={String(Math.floor((parseInt(formData.duration_minutes) || 0) / 60))}
                  onValueChange={(v) => {
                    const h = parseInt(v);
                    const m = (parseInt(formData.duration_minutes) || 0) % 60;
                    const total = h * 60 + m;
                    setFormData(prev => ({
                      ...prev,
                      duration_minutes: total,
                      scheduled_time_end: calcEndTime(prev.scheduled_time_start, total),
                    }));
                  }}
                >
                  <SelectTrigger className="w-[80px] h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 13 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>{i}ч</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={String((parseInt(formData.duration_minutes) || 0) % 60)}
                  onValueChange={(v) => {
                    const m = parseInt(v);
                    const h = Math.floor((parseInt(formData.duration_minutes) || 0) / 60);
                    const total = h * 60 + m;
                    setFormData(prev => ({
                      ...prev,
                      duration_minutes: total,
                      scheduled_time_end: calcEndTime(prev.scheduled_time_start, total),
                    }));
                  }}
                >
                  <SelectTrigger className="w-[80px] h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[0, 15, 30, 45].map(m => (
                      <SelectItem key={m} value={String(m)}>{m}м</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.scheduled_time_end && (
                <p className="text-xs text-muted-foreground">Окончание: {formData.scheduled_time_end}</p>
              )}
            </div>

            {/* Conflict Warning */}
            {conflicts.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Конфликт времени!</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc list-inside mt-1">
                    {conflicts.map((c, i) => (
                      <li key={i}>
                        <strong>{c.worker_name}</strong>: уже назначен на "{c.conflicting_order_title}" в {c.conflicting_time_start}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Address */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Адрес</Label>
                <Input
                  value={formData.address_street}
                  onChange={(e) => setFormData(prev => ({...prev, address_street: e.target.value}))}
                  placeholder="Улица, дом, кв."
                  data-testid="order-address"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Город</Label>
                  <Input
                    value={formData.address_city}
                    onChange={(e) => setFormData(prev => ({...prev, address_city: e.target.value}))}
                    placeholder="Vancouver"
                    data-testid="order-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Индекс</Label>
                  <Input
                    value={formData.address_postal}
                    onChange={(e) => setFormData(prev => ({...prev, address_postal: formatPostal(e.target.value)}))}
                    placeholder="V6B 2N9"
                    maxLength={7}
                    data-testid="order-postal"
                  />
                </div>
              </div>
            </div>

            {/* Workers Assignment - скрыть если работник уже выбран из календаря */}
            {preselectedWorker ? (
              <div className="space-y-2">
                <Label>Назначенный работник</Label>
                <div className="flex items-center gap-2 p-3 rounded-lg border bg-accent/10">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: preselectedWorker.color }}
                  />
                  <span className="font-medium">{preselectedWorker.first_name} {preselectedWorker.last_name}</span>
                  <span className="text-xs text-muted-foreground ml-auto">Выбран из календаря</span>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Назначить работников</Label>
                <div className="flex flex-wrap gap-2 p-3 rounded-lg border bg-muted/30">
                  {workers.filter(w => w.is_active && (!w.role || w.role === 'worker')).length === 0 ? (
                    <p className="text-sm text-muted-foreground">Нет доступных работников</p>
                  ) : (
                    workers.filter(w => w.is_active && (!w.role || w.role === 'worker')).map(worker => (
                      <button
                        key={worker.id}
                        type="button"
                        onClick={() => toggleWorker(worker.id)}
                        className={cn(
                          "flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors",
                          formData.assigned_worker_ids.includes(worker.id)
                            ? "bg-accent text-accent-foreground border-accent"
                            : "bg-background hover:bg-muted"
                        )}
                      >
                        <div 
                          className="w-2.5 h-2.5 rounded-full" 
                          style={{ backgroundColor: worker.color }}
                        />
                        <span className="text-sm">{worker.first_name} {worker.last_name}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            <div className="space-y-2">
              <Label>Описание</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                placeholder="Дополнительная информация о заказе..."
                rows={3}
                data-testid="order-description"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={loading} data-testid="order-submit">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Создать заказ
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <AddClientDialog
        open={createClientOpen}
        onOpenChange={setCreateClientOpen}
        onSuccess={async (newClient) => {
          try {
            const res = await api.get('/clients');
            setClients(res.data);
          } catch (e) {}
          if (newClient?.id) {
            setFormData(prev => ({
              ...prev,
              client_id: newClient.id,
              address_street: newClient.address?.street || '',
              address_city: newClient.address?.city || '',
              address_postal: newClient.address?.postal_code || '',
            }));
          }
        }}
      />
    </Dialog>
  );
}
